import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";
import type { Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { type ChangeEvent, type FC, type FormEvent, type ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { usePermissions } from "src/auth/usePermissions";
import { ROUTES } from "src/routes/paths";
import { locationDetail } from "src/workspace/labels";
import { useWorkspaceMutations, type Workspace } from "src/workspace/useWorkspace";
import type { PanelKey } from "src/workspace/workspacePanels";

type StatusFilter = "all" | "current" | "future";
const FILTERS: StatusFilter[] = ["all", "current", "future"];

const PILL_SX = { borderRadius: "999px", height: 32, fontSize: 13, fontWeight: 500 } as const;

const StatusBadge: FC<{ status: "current" | "future" }> = ({ status }) => {
	const { t } = useTranslation();
	return (
		<Box
			component="span"
			sx={{
				ml: 1,
				px: 1,
				borderRadius: "999px",
				fontSize: 12,
				fontWeight: 600,
				lineHeight: "20px",
				bgcolor: status === "current" ? "brand.selected" : "brand.nudge",
				color: status === "current" ? "primary.main" : "brand.nudgeText"
			}}>
			{t(`workspace.status.${status}`)}
		</Box>
	);
};

interface FilterChipProps {
	value: StatusFilter;
	count: number;
	selected: boolean;
	onSelect: (value: StatusFilter) => void;
}

const FilterChip: FC<FilterChipProps> = ({ value, count, selected, onSelect }) => {
	const { t } = useTranslation();
	const handleClick = useCallback(() => onSelect(value), [onSelect, value]);
	return (
		<Chip
			label={t(`workspace.requirements.filter.${value}`, { count })}
			onClick={handleClick}
			color={selected ? "primary" : "default"}
			sx={{ ...PILL_SX, borderColor: selected ? "primary.main" : undefined }}
		/>
	);
};

const Section: FC<{ title: string; detail: string; children: ReactNode }> = ({ title, detail, children }) => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns: { "@": "1fr", "@600": "220px 1fr" },
			gap: { "@": 1.5, "@600": 3 },
			py: 2.5
		}}>
		<Box>
			<Typography variant="h4" component="h3">
				{title}
			</Typography>
			<Typography variant="caption" color="text.secondary">
				{detail}
			</Typography>
		</Box>
		<Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
			{children}
		</Stack>
	</Box>
);

const AddChip: FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
	<ButtonBase
		onClick={onClick}
		sx={{
			...PILL_SX,
			px: 1.5,
			border: "1px dashed",
			borderColor: "brand.outline",
			color: "text.secondary",
			gap: 0.5
		}}>
		<AddIcon sx={{ fontSize: 16 }} />
		{label}
	</ButtonBase>
);

/** "+ Add or write in" for free-text requirements (applications, compliance). */
const TagAdder: FC<{ placeholder: string; onAdd: (value: string) => void }> = ({ placeholder, onAdd }) => {
	const [value, setValue] = useState("");
	const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setValue(event.target.value), []);
	const handleSubmit = useCallback(
		(event: FormEvent) => {
			event.preventDefault();
			if (!value.trim()) return;
			onAdd(value.trim());
			setValue("");
		},
		[onAdd, value]
	);
	return (
		<Box component="form" onSubmit={handleSubmit}>
			<InputBase
				value={value}
				onChange={handleChange}
				placeholder={placeholder}
				inputProps={{ "aria-label": placeholder }}
				sx={{ ...PILL_SX, px: 1.5, border: "1px dashed", borderColor: "brand.outline", width: 180 }}
			/>
		</Box>
	);
};

interface RequirementsDialogProps {
	workspace: Workspace;
	open: boolean;
	onClose: () => void;
}

/**
 * Figma "Map Auto-Adding" (2:1383) "Requirements context": everything known about the customer's
 * situation, grouped, with current / future status. Offices, data centers and clouds are added on
 * the map; applications and compliance are written in here.
 */
export const RequirementsDialog: FC<RequirementsDialogProps> = ({ workspace, open, onClose }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { canEditProjects, canEditSolutions, canEditWorkspaceLocations } = usePermissions();
	const fullScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
	const { updateContext } = useWorkspaceMutations();
	const [filter, setFilter] = useState<StatusFilter>("all");

	const { solution, project, locations, model, context } = workspace;
	const applications = context?.applications ?? [];
	const compliance = context?.compliance ?? [];
	const offices = locations.filter(location => location.kind === "office");
	const dataCenters = locations.filter(location => location.kind === "data_center");
	const shown = (status: "current" | "future") => filter === "all" || filter === status;

	// Clouds, applications and compliance have no status yet; they count as current.
	const undated = model.design.cloudOnramps.length + applications.length + compliance.length;
	const totals: Record<StatusFilter, number> = {
		all: locations.length + undated,
		current: locations.filter(location => location.status === "current").length + undated,
		future: locations.filter(location => location.status === "future").length
	};

	const openPanel = useCallback(
		async (panel: PanelKey) => {
			onClose();
			await navigate(`${ROUTES.solutionDesign(solution.id)}?panel=${panel}`);
		},
		[navigate, onClose, solution.id]
	);
	const addOffice = useCallback(() => void openPanel("office"), [openPanel]);
	const addDataCenter = useCallback(() => void openPanel("dataCenter"), [openPanel]);
	const addCloud = useCallback(() => void openPanel("cloud"), [openPanel]);

	const saveList = useCallback(
		(field: "applications" | "compliance", next: string[]) =>
			updateContext.mutateAsync({ projectId: project.id, data: { [field]: next } }),
		[project.id, updateContext]
	);
	const addApplication = useCallback(
		(value: string) => void saveList("applications", [...new Set([...applications, value])]),
		[applications, saveList]
	);
	const addCompliance = useCallback(
		(value: string) => void saveList("compliance", [...new Set([...compliance, value])]),
		[compliance, saveList]
	);
	const removeFrom = useCallback(
		(field: "applications" | "compliance", list: string[], value: string) => () =>
			void saveList(
				field,
				list.filter(item => item !== value)
			),
		[saveList]
	);

	const people = offices.reduce((sum, office) => sum + (office.people ?? 0), 0);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				spacing={2}
				sx={{ px: { xs: 2, sm: 3.5 }, py: 2.5, alignItems: { sm: "center" }, borderBottom: 1, borderColor: "divider" }}>
				<Typography variant="h1" component="h2" sx={{ flex: 1 }}>
					{t("workspace.requirements.title")}
				</Typography>
				<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
					{FILTERS.map(key => (
						<FilterChip key={key} value={key} count={totals[key]} selected={filter === key} onSelect={setFilter} />
					))}
					<IconButton onClick={onClose} aria-label={t("common.close")}>
						<CloseIcon />
					</IconButton>
				</Stack>
			</Stack>
			<Box sx={{ px: { xs: 2, sm: 3.5 }, pb: 2, overflowY: "auto", containerType: "inline-size" }}>
				<Section
					title={t("workspace.requirements.offices")}
					detail={t("workspace.requirements.officesDetail", { count: offices.length, people })}>
					{offices
						.filter(office => shown(office.status))
						.map(office => (
							<Chip
								key={office.id}
								label={
									<>
										{office.name} · {locationDetail(t, office)}
									</>
								}
								sx={PILL_SX}
							/>
						))}
					{canEditWorkspaceLocations && <AddChip label={t("common.add")} onClick={addOffice} />}
				</Section>
				<Divider />
				<Section
					title={t("workspace.requirements.dataCenters")}
					detail={t("workspace.requirements.dataCentersDetail", { count: dataCenters.length })}>
					{dataCenters
						.filter(dataCenter => shown(dataCenter.status))
						.map(dataCenter => (
							<Chip
								key={dataCenter.id}
								label={
									<>
										{dataCenter.name}
										{dataCenter.provider ? ` · ${dataCenter.provider}` : ""}
										<StatusBadge status={dataCenter.status} />
									</>
								}
								sx={PILL_SX}
							/>
						))}
					{canEditWorkspaceLocations && <AddChip label={t("common.add")} onClick={addDataCenter} />}
				</Section>
				<Divider />
				<Section
					title={t("workspace.requirements.clouds")}
					detail={t("workspace.requirements.cloudsDetail", { count: model.design.cloudOnramps.length })}>
					{shown("current") &&
						model.design.cloudOnramps.map(onramp => (
							<Chip
								key={onramp.cloudRegionId ?? `${onramp.provider}-${onramp.region}`}
								label={`${onramp.provider} · ${onramp.region}`}
								sx={PILL_SX}
							/>
						))}
					{canEditSolutions && <AddChip label={t("common.add")} onClick={addCloud} />}
				</Section>
				<Divider />
				<Section
					title={t("workspace.requirements.applications")}
					detail={t("workspace.requirements.applicationsDetail", { count: applications.length })}>
					{shown("current") &&
						applications.map(application => (
							<Chip
								key={application}
								label={application}
								onDelete={canEditProjects ? removeFrom("applications", applications, application) : undefined}
								sx={PILL_SX}
							/>
						))}
					{canEditProjects && <TagAdder placeholder={t("workspace.requirements.writeIn")} onAdd={addApplication} />}
				</Section>
				<Divider />
				<Section
					title={t("workspace.requirements.compliance")}
					detail={t("workspace.requirements.complianceDetail", { count: compliance.length })}>
					{shown("current") &&
						compliance.map(standard => (
							<Chip
								key={standard}
								label={standard}
								onDelete={canEditProjects ? removeFrom("compliance", compliance, standard) : undefined}
								sx={PILL_SX}
							/>
						))}
					{canEditProjects && <TagAdder placeholder={t("workspace.requirements.writeIn")} onAdd={addCompliance} />}
				</Section>
			</Box>
		</Dialog>
	);
};
